import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import {
  accessMeetsRequired,
  normalizePermissionsFromLogin,
} from '../../lib/permissions/adminFeatures'
import { INSUFFICIENT_PERMISSION_MESSAGE } from '../../lib/permissions/uiMessages'
import { InvestmentLimitRequestDetail } from '../../lib/types'
import { investmentLimitRequestServices } from '../../services'
import { RootState } from '../../store'
import { InvestmentLimitRequestDetailPresenter } from './investment-limit-request-detail-presenter'

export function InvestmentLimitRequestDetailContainer() {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { permissions, role } = useSelector((state: RootState) => state.gbcUser)

  const effectivePermissions = useMemo(
    () => permissions ?? normalizePermissionsFromLogin(role as string, null),
    [permissions, role],
  )

  const canReview = useMemo(
    () => accessMeetsRequired(effectivePermissions.kyc, 'write'),
    [effectivePermissions.kyc],
  )

  const [request, setRequest] = useState<InvestmentLimitRequestDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    setIsLoading(true)
    const data = await investmentLimitRequestServices.getById(id)
    if (data.status === 200 && data.request) {
      setRequest(data.request)
    } else {
      setRequest(null)
      dispatch(
        showBanner({
          text:
            data.code === 'investment-limit-request-not-found'
              ? 'Investment limit request not found'
              : data.message || 'Failed to load investment limit request',
          variant: 'error-banner',
        }),
      )
    }
    setIsLoading(false)
  }, [dispatch, id])

  useEffect(() => {
    void load()
  }, [load])

  const handleApprove = async (limitDollars: number, note?: string) => {
    if (!id || !request) return
    if (!canReview) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    if (!(limitDollars > 0)) {
      dispatch(
        showBanner({
          text: 'Enter a positive investment limit',
          variant: 'error-banner',
        }),
      )
      return
    }

    setIsSubmitting(true)
    try {
      const investmentLimitInCents = Math.round(limitDollars * 100)
      const data = await investmentLimitRequestServices.review(id, {
        status: 'APPROVED',
        investmentLimit: investmentLimitInCents,
        ...(note?.trim() ? { note: note.trim() } : {}),
      })
      if (data.status === 200) {
        dispatch(
          showBanner({
            text: data.result?.message || 'Request approved',
            variant: 'success-banner',
          }),
        )
        await load()
      } else {
        dispatch(
          showBanner({
            text:
              data.code === 'investment-limit-request-invalid-status'
                ? 'Only submitted requests can be reviewed'
                : data.message || 'Failed to approve request',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReject = async (note?: string) => {
    if (!id || !request) return
    if (!canReview) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }

    setIsSubmitting(true)
    try {
      const data = await investmentLimitRequestServices.review(id, {
        status: 'REJECTED',
        ...(note?.trim() ? { note: note.trim() } : {}),
      })
      if (data.status === 200) {
        dispatch(
          showBanner({
            text: data.result?.message || 'Request rejected',
            variant: 'success-banner',
          }),
        )
        await load()
      } else {
        dispatch(
          showBanner({
            text:
              data.code === 'investment-limit-request-invalid-status'
                ? 'Only submitted requests can be reviewed'
                : data.message || 'Failed to reject request',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <InvestmentLimitRequestDetailPresenter
      request={request}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      canReview={canReview}
      onBack={() => navigate('/investment-limit-requests')}
      onApprove={handleApprove}
      onReject={handleReject}
    />
  )
}
